import React, { useState } from 'react';
import { format } from 'date-fns';
import { useFamilyStore } from '../../stores/familyStore';
import { useAuthStore } from '../../stores/authStore';
import { PrimaryParentOnly } from '../security/PermissionGate';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import type { Id } from '../../../convex/_generated/dataModel';

/**
 * Component displaying list of family invitations
 */
export const InvitationsList: React.FC = () => {
  const { user } = useAuthStore();
  const { family } = useFamilyStore();
  const [error, setError] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  // Reactive Convex query — automatically updates
  const invitations = useQuery(
    api.invitations.queries.getInvitations,
    family ? { familyId: family.id as Id<"families"> } : 'skip'
  );

  const cancelInvitationMutation = useMutation(api.invitations.mutations.cancelInvitation);

  const isLoading = invitations === undefined;

  const handleCancelInvitation = async (invitationId: string) => {
    if (!user || !family) return;

    setCancellingId(invitationId);
    setError(null);

    try {
      await cancelInvitationMutation({
        invitationId: invitationId as Id<"familyInvitations">,
        familyId: family.id as Id<"families">,
      });
      // Convex reactive query will automatically refresh the list
    } catch (err: any) {
      console.error('Error cancelling invitation:', err);
      setError(err.message || 'Failed to cancel invitation');
      setTimeout(() => setError(null), 3000);
    } finally {
      setCancellingId(null);
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="badge badge-warning">Pending</span>;
      case 'accepted':
        return <span className="badge badge-success">Accepted</span>;
      case 'rejected':
        return <span className="badge badge-error">Rejected</span>;
      case 'expired':
        return <span className="badge badge-ghost">Expired</span>;
      default:
        return <span className="badge">{status}</span>;
    }
  };

  const isExpired = (expiresAt: number) => {
    return expiresAt < Date.now();
  };

  const getRoleDisplayName = (role: string): string => {
    switch (role) {
      case 'parent_a':
        return 'Parent / Guardian (Admin)';
      case 'parent_b':
        return 'Parent / Guardian';
      case 'child_viewer':
        return 'Viewer';
      default:
        return role;
    }
  };

  const getInvitationUrl = (token: string): string => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/invitation/${token}`;
  };

  return (
    <PrimaryParentOnly fallback={
      <div className="text-center py-4 text-gray-500">
        Only administrator can manage invitations
      </div>
    }>
      <div>
        <h2 className="text-lg font-semibold mb-4">Sent invitations</h2>

        {isLoading ? (
          <div className="text-center py-4">Loading invitations...</div>
        ) : error ? (
          <div className="text-center py-4 text-error">{error}</div>
        ) : !invitations || invitations.length === 0 ? (
          <div className="text-center py-4 text-gray-500">No sent invitations</div>
        ) : (
          <div className="space-y-4">
            {invitations.map((invitation: any) => (
              <div
                key={invitation._id}
                className={`border rounded-md p-3 ${
                  isExpired(invitation.expiresAt) && invitation.status === 'pending'
                    ? 'bg-gray-50'
                    : ''
                }`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-medium">{invitation.email}</div>
                    <div className="text-sm text-gray-500">
                      Role: {getRoleDisplayName(invitation.role)}
                    </div>
                    <div className="text-sm text-gray-500">
                      Expires: {format(new Date(invitation.expiresAt), 'dd MMM yyyy')}
                    </div>
                    {invitation.status === 'pending' && !isExpired(invitation.expiresAt) && (
                      <div className="text-sm mt-2 bg-blue-50 p-2 border border-blue-100 rounded-md">
                        <div className="font-medium text-blue-800 mb-1">Invitation link:</div>
                        <div className="text-blue-600 break-all text-xs font-mono bg-white p-1 rounded border mb-2">
                          {getInvitationUrl(invitation.token)}
                        </div>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(getInvitationUrl(invitation.token));
                            alert('Link copied to clipboard!');
                          }}
                          className="btn btn-xs btn-outline btn-primary"
                        >
                          Copy link
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end">
                    {getStatusLabel(
                      isExpired(invitation.expiresAt) && invitation.status === 'pending'
                        ? 'expired'
                        : invitation.status
                    )}

                    {invitation.status === 'pending' && !isExpired(invitation.expiresAt) && (
                      <button
                        onClick={() => handleCancelInvitation(invitation._id)}
                        className="btn btn-xs btn-outline btn-error mt-2"
                        disabled={cancellingId === invitation._id}
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </PrimaryParentOnly>
  );
};
